import React, { useRef, useEffect } from 'react';

export default function RichTextEditor({
  value = '',
  onChange = () => {},
  placeholder = 'Describe el avance realizado…',
  minHeight = '220px',
  showToolbar = true,
  onImageUpload = null,
  beforeImageUpload = null,
  showCounter = true,
  maxLength = 2000,
}) {
  const editorRef = useRef(null);

  // Sincronizar value externo con el contenido del editor
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const exec = (cmd) => (e) => {
    e.preventDefault();
    
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();

    switch (cmd) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'ul':
        document.execCommand('insertUnorderedList', false, null);
        break;
      case 'ol':
        document.execCommand('insertOrderedList', false, null);
        break;
      case 'code':
        document.execCommand('formatBlock', false, 'pre');
        break;
      case 'clear':
        editor.innerHTML = '';
        onChange('');
        return;
      default:
        break;
    }

    // Notificar cambio
    onChange(editor.innerHTML);
  };

  const handleInput = () => {
    const editor = editorRef.current;
    if (!editor) return;

    let content = editor.innerHTML;

    // Aplicar límite de caracteres si está configurado
    if (maxLength) {
      const plainText = editor.innerText || editor.textContent || '';
      if (plainText.length > maxLength) {
        // Revertir al valor anterior
        editor.innerHTML = value;
        return;
      }
    }

    onChange(content);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const base = {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff',
  };

  const btnSm = {
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 6,
    padding: '4px 8px', 
    lineHeight: 1, 
    fontSize: 13,
    border: '1px solid #e5e7eb', 
    borderRadius: 999, 
    background: '#f8fafc',
    cursor: 'pointer',
  };

  const toolbar = (
    <div style={{
      display:'flex', 
      gap:8, 
      padding:8, 
      borderBottom:'1px solid #e5e7eb',
      flexWrap:'wrap', 
      background:'#f9fafb', 
      borderTopLeftRadius:12, 
      borderTopRightRadius:12
    }}>
      <button type="button" style={btnSm} onClick={exec('bold')} title="Negrita">
        <b>B</b>
      </button>
      <button type="button" style={btnSm} onClick={exec('italic')} title="Cursiva">
        <i>I</i>
      </button>
      <div style={{ width:1, background:'#e5e7eb', margin:'0 4px' }} />
      <button type="button" style={btnSm} onClick={exec('ul')} title="Lista •">
        •
      </button>
      <button type="button" style={btnSm} onClick={exec('ol')} title="Lista 1.">
        1.
      </button>
      <button type="button" style={btnSm} onClick={exec('code')} title="Código">
        {`</>`}
      </button>
      <div style={{ flex:1 }} />
      <button type="button" style={btnSm} onClick={exec('clear')} title="Limpiar">
        Limpiar
      </button>
    </div>
  );

  const plainTextLength = editorRef.current 
    ? (editorRef.current.innerText || editorRef.current.textContent || '').length 
    : 0;
  const remaining = maxLength ? Math.max(0, maxLength - plainTextLength) : null;

  const showPlaceholder = !value || value === '' || editorRef.current?.innerText?.trim() === '';

  return (
    <div style={base}>
      {showToolbar && toolbar}

      {/* Área editable */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning
        style={{
          width:'100%', 
          minHeight, 
          padding:12, 
          border:'none', 
          outline:'none',
          fontFamily:'inherit', 
          fontSize:14, 
          lineHeight:1.5,
          borderBottom:'1px dashed #eef2f7',
          overflowY: 'auto',
        }}
        data-placeholder={showPlaceholder ? placeholder : ''}
      />

      {/* Contador */}
      {showCounter && maxLength && (
        <div style={{ 
          padding:'10px 12px', 
          background:'#fafafa', 
          borderBottomLeftRadius:12, 
          borderBottomRightRadius:12,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <div style={{ fontSize:12, opacity:.7 }}>
            {remaining} caract.
          </div>
        </div>
      )}

      <style>{`
        [contentEditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          position: absolute;
        }
        [contentEditable] ul {
          padding-left: 20px;
          list-style-type: disc;
        }
        [contentEditable] ol {
          padding-left: 20px;
          list-style-type: decimal;
        }
        [contentEditable] pre {
          background: #f3f4f6;
          padding: 8px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          overflow-x: auto;
        }
        [contentEditable]:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
}